/**
 * EcosystemSwitcher — "Products ▾" dropdown listing every Colony product
 * with its icon, name, and tagline. Mounts in the marketing-website
 * header so visitors on www.parcelpuffin.com can discover and navigate
 * to www.portpuffin.com, www.plannerpuffin.com, etc.
 *
 * Patterns the LocaleSwitcher's WAI-ARIA listbox shape: button toggles
 * a listbox; ArrowDown / ArrowUp / Home / End keyboard nav; Esc + click
 * outside dismiss. Each option is an anchor that navigates to a sibling
 * product's marketing site (target="_blank" is intentionally NOT set —
 * the user is fully transitioning between products, not popping a tab).
 *
 * Reads ECOSYSTEM_APPS registry. The button highlights the entry whose
 * `appKey` matches the `currentAppKey` prop.
 *
 * **POPOVER-FACTORY-001:** the open/positioning/dismiss machinery rides the
 * canonical `@puffin-core/popover` factory (DRP-001) — open-on-click toggle
 * (`useClick`), outside-click/Escape dismiss (`useDismiss`), and positioning
 * (`position: fixed` via floating-ui) all come from the factory, NOT a
 * hand-rolled `position:absolute` + document `mousedown` listener. The trigger
 * advertises `aria-haspopup="listbox"` via `triggerHasPopup="listbox"`.
 *
 * **POPOVER-LISTNAV-001:** the roving keyboard navigation (Arrow / Home / End +
 * roving focus) now rides the factory's opt-in `listNavigation` API instead of a
 * hand-rolled `handleItemKeyDown` switch — the consumer holds `activeIndex` +
 * `itemRefs` (wired into the descriptor's `listRef`) and spreads `itemProps(i)`
 * onto each option anchor; floating-ui owns Arrow/Home/End and moves focus to the
 * active item. Only the behaviors orthogonal to roving nav are kept local: the
 * `setTimeout` focus-on-open landing spot, the ArrowUp/Down-opens-the-listbox
 * trigger nicety, and the Space-synthesizes-anchor-click vs Enter-native
 * activation distinction (anchors activate on Enter natively; Space would scroll
 * the page, so we intercept it).
 *
 * @module @puffin-core/marketing-shell/EcosystemSwitcher
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Popover, PopoverContent } from '@puffin-core/popover';
import { resolveBrandTagline } from '@puffin-core/brand-assets';
import { useMarketingShellTranslate } from '../hooks/useMarketingShellTranslate.js';
import { ECOSYSTEM_APPS } from '../registries/ecosystemApps.js';

const KEY_SPACE = ' ';

/**
 * @param {Object} props
 * @param {string} props.currentAppKey  The hosting app's appKey (e.g. 'parcel').
 *
 * Its label is marketing-shell's own word (`marketingShell` namespace, `ecosystemSwitcher.label`) —
 * not a prop: AAS-I18N-002 ruling 71 found that a shared component taking its own copy as a prop
 * renders English wherever a consumer forgets it, and LIFE-SHORTLISTED-001 F-Q7-1 removed the
 * override that nine sites passed the same word through.
 */
export default function EcosystemSwitcher({ currentAppKey }) {
  const itemRefs = useRef([]);
  const [isOpen, setIsOpen] = useState(false);
  const currentIndex = ECOSYSTEM_APPS.findIndex((a) => a.appKey === currentAppKey);
  const initialActive = currentIndex >= 0 ? currentIndex : 0;
  const [activeIndex, setActiveIndex] = useState(initialActive);

  // Focus the active option whenever the popover opens (mouse click OR the
  // ArrowUp/ArrowDown open path). The factory owns the open toggle + dismiss +
  // the Arrow/Home/End roving navigation; this only manages the roving-focus
  // landing spot on open (floating-ui's `focusItemOnOpen` does not reliably
  // focus the selected item on a mouse-driven open). setTimeout(0) waits for
  // the popover to render the option elements before focusing.
  useEffect(() => {
    if (!isOpen) return undefined;
    const id = setTimeout(() => {
      itemRefs.current[activeIndex]?.focus();
    }, 0);
    return () => clearTimeout(id);
  }, [isOpen, activeIndex]);

  // Per-item key handling that is ORTHOGONAL to the factory's roving navigation:
  // Arrow/Home/End now bubble to the listbox's factory keydown handler (from
  // `itemProps` + the floating element), so only the anchor-activation nuance is
  // kept local. Default anchor behavior handles Enter activation natively; we
  // intercept Space (which would otherwise scroll the page when the anchor has
  // focus) and synthesize the anchor click.
  const handleItemKeyDown = useCallback((event, index) => {
    if (event.key === KEY_SPACE) {
      event.preventDefault();
      itemRefs.current[index]?.click();
    }
  }, []);

  // AAS-I18N-002 Q-F4 — sibling-product taglines are COPY. `useTranslation` is
  // the subscription that re-renders this list on a language switch, and
  // `i18n.language` is read at RENDER so the dropdown never serves the boot
  // locale's copy on a page the visitor has since switched.
  //
  // ⚠️ Position is deliberate (AAS-I18N-002 RECIPE §3): `useTranslation` is a
  // CUSTOM hook, so `web/component-state-order` wants it AFTER the computed
  // hooks — placed at the top of the component it warns once per `t`-free
  // `useCallback` below it. Nothing here consumes `i18n` inside a hook, so the
  // last position satisfies the rule with no TDZ risk.
  const { i18n } = useTranslation();
  const t = useMarketingShellTranslate();
  const resolvedLabel = t('ecosystemSwitcher.label');

  return (
    <div className="ecosystem-switcher">
      <Popover
        open={isOpen}
        onOpenChange={setIsOpen}
        placement="bottom-end"
        modal={false}
        triggerHasPopup="listbox"
        listNavigation={{
          listRef: itemRefs,
          activeIndex,
          onNavigate: setActiveIndex,
          loop: true,
          selectedIndex: initialActive,
        }}
      >
        {/* Trigger Arrow-key behavior (ArrowUp/ArrowDown opens the listbox,
            with preventDefault page-scroll suppression) is OWNED by the
            popover factory: floating-ui's useListNavigation opens on trigger
            arrow keydown by default. A local capture-phase twin existed here
            and was PRUNED after a probe showed the suite (incl. the
            preventDefault pin) green without it (Q-CGI-K25-1). */}
        <button type="button" className="ecosystem-switcher-button" aria-label={resolvedLabel}>
          <span>{resolvedLabel}</span>
        </button>
        <PopoverContent
          className="ecosystem-switcher-popover"
          ariaLabel={resolvedLabel}
          testId="ecosystem-switcher-popover"
        >
          {({ itemProps }) => (
            <ul
              className="ecosystem-switcher-menu"
              role="listbox"
              aria-label={resolvedLabel}
              tabIndex={-1}
            >
              {ECOSYSTEM_APPS.map((app, index) => {
                const isCurrent = app.appKey === currentAppKey;
                const className = isCurrent
                  ? 'ecosystem-switcher-option ecosystem-switcher-option-current'
                  : 'ecosystem-switcher-option';
                return (
                  <li key={app.appKey} role="option" aria-selected={isCurrent}>
                    <a
                      ref={(el) => {
                        itemRefs.current[index] = el;
                      }}
                      href={app.marketingUrl}
                      className={className}
                      tabIndex={index === activeIndex ? 0 : -1}
                      {...itemProps(index)}
                      onKeyDown={(event) => handleItemKeyDown(event, index)}
                    >
                      <img
                        src={app.colonyGlyph}
                        alt=""
                        className="ecosystem-switcher-option-icon"
                        loading="lazy"
                        aria-hidden="true"
                      />
                      <span className="ecosystem-switcher-option-text">
                        <span className="ecosystem-switcher-option-name">{app.appName}</span>
                        <span className="ecosystem-switcher-option-tagline">
                          {resolveBrandTagline(app, i18n?.language)}
                        </span>
                      </span>
                    </a>
                  </li>
                );
              })}
            </ul>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}

EcosystemSwitcher.propTypes = {
  currentAppKey: PropTypes.string.isRequired,
};
